import { cloneElement } from "react";

import { useImageResolver } from "@/api/hooks/useImageResolver";
import type { ImageDto } from "@/api/types";
import type { IconComponent } from "@/components/Icon/Icon";
import { ChevronRightIcon } from "@/components/Icons/ChevronRightIcon";
import { FaceFrownIcon } from "@/components/Icons/FaceFrownIcon";
import { Inline } from "@/design-system/Inline";
import { Skeleton } from "@/design-system/Skeleton";
import { useTranslation } from "@/translations";

import { EntityPreviewCardContainer } from "./EntityPreviewCardWithBackground";

type EntityPreviewCardProps = {
  title: string;
  href?: string;
  onClick?: () => void;
  renderBadge?: () => React.ReactNode;
} & (
  | {
      image: ImageDto | undefined;
      icon?: never;
    }
  | {
      image?: never;
      icon: IconComponent;
    }
) &
  (
    | {
        subtitle?: string;
        renderSubtitle?: never;
      }
    | {
        subtitle?: never;
        renderSubtitle: () => React.ReactNode | undefined;
      }
  );

export function EntityPreviewCard({
  title,
  href,
  onClick,
  image,
  icon,
  subtitle,
  renderSubtitle,
  renderBadge,
}: EntityPreviewCardProps): React.ReactNode {
  const resolveImage = useImageResolver();

  return (
    <EntityPreviewCardContainer href={href} onClick={onClick}>
      <section className="flex items-center gap-space-sm p-space-sm">
        {(image || icon) && (
          <span
            id="entity-preview-card-thumbnail"
            className="flex size-thumbnail-prominent items-center justify-center overflow-hidden rounded-md border border-border-default bg-background-secondary"
          >
            {image && <img src={resolveImage(image, "md")} alt={title} className="size-full object-cover" />}
            {icon && cloneElement(icon, { size: 24 })}
          </span>
        )}
        <section className="flex flex-1 flex-col justify-center gap-space-xs">
          {renderBadge && (
            <Inline align="start" wrap={false}>
              {renderBadge()}
            </Inline>
          )}
          <h4 className="line-clamp-1 text-body-emphasis">{title}</h4>
          {subtitle ? <p className="line-clamp-1 text-caption text-text-secondary">{subtitle}</p> : renderSubtitle?.()}
        </section>
        {href && (
          <span className="h-fit">
            <ChevronRightIcon size={24} tone="secondary" />
          </span>
        )}
      </section>
    </EntityPreviewCardContainer>
  );
}

export function EntityPreviewCardSkeleton(): React.ReactNode {
  return (
    <EntityPreviewCardContainer>
      <section className="flex items-center gap-space-sm p-space-sm">
        <Skeleton height="16" width="16" />
        <section className="flex flex-1 flex-col justify-center gap-space-xs">
          <Skeleton height="5" width="1/3" />
          <Skeleton height="4" width="2/3" />
        </section>
      </section>
    </EntityPreviewCardContainer>
  );
}

export type EntityPreviewCardErrorProps = {
  title?: string;
  subtitle?: string;
};

export function EntityPreviewCardError({ title, subtitle }: EntityPreviewCardErrorProps): React.ReactNode {
  const { t } = useTranslation();

  const titleOverride = title || t("component.community-post.entity-preview.title.not-available");
  const subtitleOverride = subtitle || t("component.community-post.entity-preview.subtitle.not-available");

  return (
    <section className="w-full *:text-text-secondary">
      <EntityPreviewCard title={titleOverride} subtitle={subtitleOverride} icon={<FaceFrownIcon tone="secondary" />} />
    </section>
  );
}
